import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, MapPin, FileText, StickyNote, Save, User, Phone, Mail, Trash2, PackageCheck } from 'lucide-react';
import { Card, Button, Input, Modal, LoadingSpinner, ErrorMessage } from '@/components/ui';
import { OrderStatusBadge, StatusTimeline } from '@/components/features/orders';
import ReHandoffModal from '@/components/features/orders/ReHandoffModal';
import { ordersApi } from '@/services/api/ordersApi';
import { queryKeys } from '@/services/api/queryClient';
import { useAuthStore } from '@/stores/authStore';
import { UserRole, OrderStatus } from '@/types/enums';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orderId = Number(id);

  const { user } = useAuthStore();
  const [shelfInput, setShelfInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [shelfEditing, setShelfEditing] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);
  const [showCustomer, setShowCustomer] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [reHandoffOpen, setReHandoffOpen] = useState(false);

  const { data: order, isLoading, error } = useQuery({
    queryKey: queryKeys.order(orderId),
    queryFn: () => ordersApi.getById(orderId),
    enabled: !isNaN(orderId),
  });

  const shelfMutation = useMutation({
    mutationFn: (shelf_location: string) => ordersApi.assignShelf(orderId, shelf_location),
    onSuccess: () => {
      toast.success('Полка назначена');
      queryClient.invalidateQueries({ queryKey: queryKeys.order(orderId) });
      setShelfEditing(false);
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при назначении полки')),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { notes?: string | null }) => ordersApi.updateOrder(orderId, data),
    onSuccess: () => {
      toast.success('Заметка сохранена');
      queryClient.invalidateQueries({ queryKey: queryKeys.order(orderId) });
      setNotesEditing(false);
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при сохранении')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => ordersApi.deleteOrder(orderId),
    onSuccess: () => {
      toast.success('Заказ удалён');
      queryClient.invalidateQueries({ queryKey: queryKeys.orders });
      navigate('/orders');
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Ошибка при удалении заказа')),
  });

  if (isLoading) return <LoadingSpinner size="lg" className="py-24" />;
  if (error || !order) return <ErrorMessage message="Заказ не найден" />;

  const handleSaveShelf = () => {
    if (shelfInput.trim()) {
      shelfMutation.mutate(shelfInput.trim());
    }
  };

  const handleSaveNotes = () => {
    updateMutation.mutate({ notes: notesInput || null });
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/orders')}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Заказ {order.order_number}</h1>
            <OrderStatusBadge status={order.status} size="md" />
          </div>
          {order.invoice_number && (
            <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" />
              Накладная: {order.invoice_number}
            </p>
          )}
        </div>
        {order.status === OrderStatus.PARTIALLY_HANDED_OFF && (
          <Button
            variant="outline"
            size="sm"
            className="!text-amber-600 !border-amber-200 hover:!bg-amber-50"
            onClick={() => setReHandoffOpen(true)}
          >
            <PackageCheck className="w-4 h-4" />
            Довыдать
          </Button>
        )}
        {user?.role === UserRole.MANAGER && order.status === OrderStatus.ARRIVED && (
          <Button
            variant="outline"
            size="sm"
            className="!text-red-600 !border-red-200 hover:!bg-red-50"
            onClick={() => { setDeleteConfirmed(false); setDeleteModalOpen(true); }}
          >
            <Trash2 className="w-4 h-4" />
            Удалить
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-gray-900">Позиции заказа</h2>
            </Card.Header>
            <Card.Body className="p-0">
              {order.items.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">Нет позиций</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Артикул</th>
                        <th className="px-4 py-3 text-left font-semibold">Наименование</th>
                        <th className="px-4 py-3 text-right font-semibold">Заказано</th>
                        <th className="px-4 py-3 text-right font-semibold">Принято</th>
                        <th className="px-4 py-3 text-right font-semibold">Выдано</th>
                        <th className="px-4 py-3 text-right font-semibold">Цена</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {order.items.map((item) => {
                        const hasShortage = item.quantity_received < item.quantity_ordered;
                        const handedOffAll = item.quantity_handed_off >= item.quantity_received;
                        const handedOffPartial = item.quantity_handed_off > 0 && !handedOffAll;
                        const remaining = item.quantity_received - item.quantity_handed_off;
                        return (
                          <tr key={item.id} className={cn(hasShortage && 'bg-red-50/50')}>
                            <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.sku}</td>
                            <td className="px-4 py-3 text-gray-900">{item.name}</td>
                            <td className="px-4 py-3 text-right">{item.quantity_ordered}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={cn(hasShortage && 'text-red-600 font-semibold')}>
                                {item.quantity_received}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={cn(
                                'font-semibold',
                                handedOffAll ? 'text-emerald-600' : handedOffPartial ? 'text-amber-600' : 'text-gray-400',
                              )}>
                                {item.quantity_handed_off}
                              </span>
                              {remaining > 0 && item.quantity_handed_off > 0 && (
                                <p className="text-[11px] text-gray-400 mt-0.5">(осталось: {remaining})</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500">
                              {item.price_per_unit != null ? `${item.price_per_unit.toFixed(2)} ₽` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  Полка
                </h2>
                {!shelfEditing && (
                  <Button size="sm" variant="ghost" onClick={() => { setShelfInput(order.shelf_location ?? ''); setShelfEditing(true); }}>
                    {order.shelf_location ? 'Изменить' : 'Назначить'}
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {shelfEditing ? (
                <div className="flex gap-3">
                  <Input
                    value={shelfInput}
                    onChange={(e) => setShelfInput(e.target.value)}
                    placeholder="Например: A-12-3"
                    className="flex-1"
                  />
                  <Button onClick={handleSaveShelf} loading={shelfMutation.isPending} size="sm">
                    <Save className="w-4 h-4" />
                    Сохранить
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShelfEditing(false)}>
                    Отмена
                  </Button>
                </div>
              ) : (
                <p className={cn('text-sm', order.shelf_location ? 'text-gray-900 font-medium' : 'text-gray-400')}>
                  {order.shelf_location || 'Полка не назначена'}
                </p>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <StickyNote className="w-4 h-4 text-gray-400" />
                  Заметки
                </h2>
                {!notesEditing && (
                  <Button size="sm" variant="ghost" onClick={() => { setNotesInput(order.notes ?? ''); setNotesEditing(true); }}>
                    {order.notes ? 'Изменить' : 'Добавить'}
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {notesEditing ? (
                <div className="space-y-3">
                  <textarea
                    value={notesInput}
                    onChange={(e) => setNotesInput(e.target.value)}
                    placeholder="Заметки по заказу..."
                    rows={3}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 placeholder-gray-400 resize-none"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleSaveNotes} loading={updateMutation.isPending} size="sm">
                      <Save className="w-4 h-4" />
                      Сохранить
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setNotesEditing(false)}>
                      Отмена
                    </Button>
                  </div>
                </div>
              ) : (
                <p className={cn('text-sm whitespace-pre-wrap', order.notes ? 'text-gray-700' : 'text-gray-400')}>
                  {order.notes || 'Нет заметок'}
                </p>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="space-y-6">
          {(order.customer_name || order.customer_phone || order.customer_email) && (
            <Card>
              <Card.Header>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    Клиент
                  </h2>
                  <Button size="sm" variant="ghost" onClick={() => setShowCustomer(!showCustomer)}>
                    {showCustomer ? 'Скрыть' : 'Показать'}
                  </Button>
                </div>
              </Card.Header>
              {showCustomer && (
                <Card.Body>
                  <dl className="space-y-2.5 text-sm">
                    {order.customer_name && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400 shrink-0" />
                        <dd className="font-medium text-gray-900">{order.customer_name}</dd>
                      </div>
                    )}
                    {order.customer_phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                        <dd>
                          <a href={`tel:${order.customer_phone}`} className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
                            {order.customer_phone}
                          </a>
                        </dd>
                      </div>
                    )}
                    {order.customer_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                        <dd>
                          <a href={`mailto:${order.customer_email}`} className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
                            {order.customer_email}
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>
                </Card.Body>
              )}
            </Card>
          )}

          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-gray-900">История статусов</h2>
            </Card.Header>
            <Card.Body>
              <StatusTimeline
                currentStatus={order.status}
                createdAt={order.created_at}
                handedOffAt={order.handed_off_at}
              />
            </Card.Body>
          </Card>

          <Card padding="md">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Информация</h3>
            <dl className="space-y-2.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">ID</dt>
                <dd className="font-medium text-gray-900">{order.id}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Создан</dt>
                <dd className="font-medium text-gray-900">{formatDate(order.created_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Принят</dt>
                <dd className="font-medium text-gray-900">{formatDate(order.received_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Выдан</dt>
                <dd className="font-medium text-gray-900">{formatDate(order.handed_off_at)}</dd>
              </div>
              {order.consolidated_delivery_id && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Поставка</dt>
                  <dd className="font-medium text-gray-900">#{order.consolidated_delivery_id}</dd>
                </div>
              )}
            </dl>
          </Card>
        </div>
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Удаление заказа"
        size="sm"
      >
        <div className="space-y-4">
          <div className="p-3 rounded-xl bg-red-50 border border-red-100">
            <p className="text-sm text-red-700">
              Заказ <span className="font-semibold">{order.order_number}</span> и все его позиции будут удалены безвозвратно.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={deleteConfirmed}
              onChange={(e) => setDeleteConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-700">
              Я подтверждаю удаление этого заказа
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setDeleteModalOpen(false)}
            >
              Отмена
            </Button>
            <Button
              className="flex-1 !bg-red-600 hover:!bg-red-700 !text-white"
              disabled={!deleteConfirmed}
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              <Trash2 className="w-4 h-4" />
              Удалить
            </Button>
          </div>
        </div>
      </Modal>

      {order.status === OrderStatus.PARTIALLY_HANDED_OFF && (
        <ReHandoffModal
          isOpen={reHandoffOpen}
          onClose={() => setReHandoffOpen(false)}
          order={order}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.order(orderId) });
            setReHandoffOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default OrderDetailPage;
